import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useRequest } from '../../hooks/UseRequest'
import { selectModules, fetchModules } from '../../redux/moduleSlice'
import LayoutSidebar from '../../components/LayoutSidebar'
import Loading from '../../components/Loading'
import { DATE_FIELDS, MODULE_FIELD_TYPES } from '../../constants'
import SubheaderDivider from '../../components/elements/SubheaderDivider'
import SpecialInstruction from '../../components/elements/SpecialInstruction'
import FormFieldBuilder from '../../components/FormFieldBuilder'
import { selectDropdownOptions, selectDropdowns, fetchDropdowns } from '../../redux/dropdownSlice'
import { generateErrorBody, handleErrorResponse } from '../../helpers/errorHandling'
import { formDataSanitizer, validateSpecialForm, getConditionalWatches, getFieldRenderRules } from '../../helpers/formValidation'
import { hasUserPermission } from '../../utils/permissionHelper'
import { selectUser } from '../../redux/userSlice'
import { setNotification } from '../../redux/notificationSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectAuth } from "../../redux/authSlice"
import { faQuestionCircle, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'
/**
 * This form is just the create form for a given module. The edit form utilizes the same core components, but is part
 * of the detail view. Any shared logic between the create and edit forms has been abstracted out of these files.
 * @returns {JSX.Element}
 * @constructor
 */
function ModuleFormInner() {
  let countDivider = 0
  const [module, setModule] = useState()
  const [redirectTo, setRedirectTo] = useState(false)
  const [moduleFields, setModuleFields] = useState()
  const [saving, setSaving] = useState(false)
  const requests = useRequest()
  const params = useParams()
  const navi = useNavigate()
  const location = useLocation();
  const modules = useSelector(selectModules)
  const dropDowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const active = useSelector(selectUser)
  const [sourceModule, setSourceModule] = useState(null)
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const [stateFields, setStateFields] = useState([]);
  const handleStateFields = (name, value) => {
    const stateFieldName = `${module.code_key}_${name}`;
    if (stateFields.some((item) => item.name === stateFieldName)) {
      setStateFields(stateFields.map((item) => {
        if (stateFieldName === item?.name) {
          return {
            ...item,
            value: value
          }
        } else {
          return item
        }
      }))
    } else {
      setStateFields([
        ...stateFields,
        {
          name: stateFieldName,
          value: value
        }
      ])
    }
  }

  /**
   * Check if the user has correct permissions
   */
  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      let userPermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      if (!hasUserPermission(userPermissions, `${module.code_key}_create`)) {
        navi('/403')
      }
    }
  }, [active, module])

  useEffect(() => {
    if (auth.accessToken) {
      if (modules.data.length > 0) {
        const m = modules.data.find((e) => e.code_key === params.module_name)

        if (m) {
          setModule(m)

          // Sort Fields
          const mf = [...m.module_fields]
          mf.sort((a, b) => a.detail_display_rank - b.detail_display_rank)
          setModuleFields(mf)
        } else {
          navi('/404')
        }
      }
      if (location.state?.source?.column_name) {
        setSourceModule({
          column_name: location.state?.source?.column_name,
          id: location.state?.source?.id,
          name: location.state?.source?.name,
          module_code_key: location.state?.source?.modele_code_key
        })
      }
      if (!redirectTo && location.state?.redirect_to) {
        setRedirectTo(location.state?.redirect_to)
        window.history.replaceState({}, document.title)
      }
    }
  }, [modules.data, params.module_name, location, auth])

  const {
    register, handleSubmit, control, setError, formState: { errors }, watch
  } = useForm();

  const dispatchErorr = () => {
    if (Object.keys(errors).length > 0) {
      const body = generateErrorBody(errors, moduleFields)
      dispatch(setNotification({ type: 'error', body: body }))
    }
  }

  const beforeFormSubmit = (event) => {
    event.preventDefault();
    dispatchErorr()
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (formData) => {
    const formError = validateSpecialForm(module.code_key, formData, dropdownOptions, setError);
    if (formError) return

    setSaving(true)
    const sanitizedData = formDataSanitizer(formData, module, dropdownOptions)
    requests.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/${module.code_key}`
    )

    try {
      const response = await requests.post(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        if (module.code_key === 'module' || module.code_key === 'module_field') {
          dispatch(fetchModules(auth.accessToken))
        } else if (module.code_key === 'dropdown' || module.code_key === 'dropdown_option') {
          dispatch(fetchDropdowns(auth.accessToken))
        }
        if (redirectTo) {
          navi(redirectTo, { state: { redirectedData: 'Successfully Save your changes' } })
        } else {
          navi(`/${module.code_key}/${response.data.data.id}`, { state: { module_data: response.data.data, redirectedData: 'Successfully Save your changes' } })
        }
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (error?.response?.status === 500) {
        dispatch(setNotification({ type: 'error', body: 'There was an error submitting' }))
      } else {
        handleErrorResponse(error.response?.data?.errors, setError)
      }
      setSaving(false)
    }
  }

  useEffect(() => {
    dispatchErorr()
  }, [errors, saving])

  if (modules && module) {
    // Special-field reveal rules. Create defaults are false (email-channel toggle) / null — unlike edit,
    // which defaults to the record's values. See SPECIAL_FIELD_RULES + getFieldRenderRules.
    const watchedValues = getConditionalWatches(module.code_key, true, watch, (name) => (name === 'is_channel_email' ? false : null))

    const contentTitle = (
      <>
        {`${module.name} Form`}
        <span className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary" data-tooltip={module.helper_text}>
          <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
        </span>
      </>
    )
    return (
      <LayoutSidebar
        contentTitle={contentTitle}
        content={(
          <form onSubmit={beforeFormSubmit} className="lox-form px-1 py-1">
            <div className="columns is-multiline pt-3">
              {moduleFields.map((m) => {
                const fieldType = dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key
                // Filter ID, Date and Show More Columns
                if (DATE_FIELDS.includes(m.column_name) || m.column_name === 'id') return ''
                if (fieldType === MODULE_FIELD_TYPES.SHOWMORE.code_key) return ''

                const selectOptions = {
                  dropdownOptions: dropDowns?.data.find((e) => m.dropdown_key === e.code_key)?.dropdown_options,
                  is_editable: (m.is_editable && !m.is_dev_only) || (m.is_editable && m.is_dev_only && active?.data?.user_role_label == 'Developers') || !params.id,
                  fieldType,
                }

                if (fieldType === MODULE_FIELD_TYPES.SUBHEADER.code_key) {
                  countDivider += 1
                  return (
                    <SubheaderDivider order={countDivider} name={m.label_name} key={m.id} />
                  )
                }

                if (fieldType === MODULE_FIELD_TYPES.SPECIAL_INSTRUCTIONS.code_key) {
                  return <SpecialInstruction name={m.label_name} key={m.id} content={m.helper_text} />
                }

                // Conditional show/hide is centralized in SPECIAL_FIELD_RULES.
                let fieldNotice = null;
                const { hidden } = getFieldRenderRules(m, watchedValues, dropdownOptions, true)
                const showFormFieldBuilder = !hidden

                if (m.module_code_key === "exclusion_list" && m.column_name === "bank_routing_number_id") {
                  fieldNotice = (
                    <p>
                      <Link target="_blank" to="/bank_routing_number/form">
                        Create New Bank Routing Number
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="is-size-12 ml-1" />
                      </Link>
                    </p>
                  );
                }

                return (showFormFieldBuilder && (
                  <FormFieldBuilder
                    field={m}
                    selectOptions={selectOptions}
                    register={register}
                    errors={errors}
                    control={control}
                    key={m.id}
                    row={moduleFields}
                    sourceModule={sourceModule}
                    fieldNotice={fieldNotice}
                    handleStateFields={handleStateFields}
                    stateFields={stateFields}
                  />
                ))
              })}
            </div>
            <div className="lox-form__buttons-wrapper has-text-right">
              <span
                className="has-text-weight-medium has-text-link"
                aria-hidden="true"
                onClick={() => {
                  return (redirectTo) ? navi(redirectTo) : navi(`/${module.code_key}`)
                }}
              >
                Cancel
              </span>
              <button
                type="submit"
                className="button is-link is-small ml-5"
                disabled={saving && 'disabled'}
              >
                {saving ? (
                  <>
                    Saving
                    <Loading size="is-size-6" />
                  </>
                ) : 'Save'}
              </button>
            </div>
          </form>
        )}
      />
    )
  }
  return <Loading />
}

/**
 * `/:module_name/form` is a single route, so React reuses this component when
 * the user moves between different modules' create forms. Keying the inner form
 * on the module forces a fresh `react-hook-form` instance per module, so a value
 * entered for one module (notably Controller-bound dropdowns, which RHF retains
 * across unmount) can never leak into the next module's create. See the
 * "Per-Record Form State (the stale-data trap)" note in docs/MODULE_SYSTEM.md
 * and the regression in ModuleForm.test.jsx.
 */
export default function ModuleForm() {
  const params = useParams()
  return <ModuleFormInner key={params.module_name} />
}
